import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import BabyProfileForm from '../../components/baby-profile/BabyProfileForm';
import { useBabyProfiles } from '../../contexts/BabyProfileContext';

// Styled Components
const PageContainer = styled.div`
  background-color: var(--light);
  padding: 2rem 0;
`;

const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  .back-link {
    color: var(--primary);
    text-decoration: none;
    display: flex;
    align-items: center;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  
  .spinner-border {
    color: var(--primary);
    margin-bottom: 1rem;
  }
`;

const ErrorContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  text-align: center;
  
  h3 {
    color: var(--primary);
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1.5rem;
  }
`;

const EditChild = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { profiles, loading, error, getProfile, refreshProfiles } = useBabyProfiles();
  const [childProfile, setChildProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Fetch the specific child profile when the component mounts
  useEffect(() => {
    const fetchChildProfile = async () => {
      try {
        setIsLoading(true);
        // First try to find the profile in the existing profiles
        let profile = profiles.find(p => p.profileId === profileId);
        
        // If not found in existing profiles, fetch it directly
        if (!profile) {
          profile = await getProfile(profileId);
        }
        
        if (!profile) {
          throw new Error('Profile not found');
        }
        
        // Check if edit limit has been reached
        if (profile.editCount && profile.editCount >= 3) {
          throw new Error('Edit limit reached: Profile can only be edited 3 times');
        }
        
        // Format the data to match the form structure
        const formattedProfile = {
          ...profile,
          profileId: profile.profileId || profileId,
          birthDate: profile.dateOfBirth || profile.birthDate || '',
          bedtime: profile.sleepHabits?.bedtime || '',
          wakeupTime: profile.sleepHabits?.wakeupTime || '',
          numberOfNaps: String(profile.sleepHabits?.numberOfNaps || ''),
          napDurations: profile.sleepHabits?.napDurations || '',
          selfSettles: profile.sleepHabits?.selfSettles || '',
          sleepAssociations: profile.sleepHabits?.sleepAssociations || {
            rocked: false,
            dummy: false,
            fedToSleep: false,
            independentlySleeps: false
          },
          feedingType: profile.feedingInfo?.feedingType || '',
          overnightFeeds: profile.feedingInfo?.overnightFeeds || '',
          fallsAsleepDuringFeeds: profile.feedingInfo?.fallsAsleepDuringFeeds || '',
          wakesMultipleTimes: profile.feedingInfo?.wakesMultipleTimes || '',
          responseToWakes: profile.feedingInfo?.responseToWakes || {
            feed: false,
            rock: false,
            dummy: false,
            cuddle: false,
            wait: false,
            other: false
          },
          responseToWakesOther: profile.feedingInfo?.responseToWakesOther || '',
          sleepLocation: profile.sleepEnvironment?.sleepLocation || '',
          isDarkRoom: profile.sleepEnvironment?.isDarkRoom || '',
          usesWhiteNoise: profile.sleepEnvironment?.usesWhiteNoise || '',
          sleepwear: profile.sleepEnvironment?.sleepwear || '',
          roomTemperature: profile.sleepEnvironment?.roomTemperature || '',
          roomSharing: profile.sleepEnvironment?.roomSharing || '',
          parentingStylePreference: profile.healthInfo?.parentingStylePreference || '',
          mainGoals: profile.healthInfo?.mainGoals || {
            longerStretches: false,
            selfSettling: false,
            betterNaps: false,
            reducingNightWakings: false
          },
          extraNotes: profile.healthInfo?.extraNotes || '',
          isPremature: profile.healthInfo?.isPremature || false,
          correctedAge: profile.healthInfo?.correctedAge || '',
          editCount: profile.editCount || 0
        };
        
        setChildProfile(formattedProfile);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching child profile:', error);
        setLoadError(error.message || 'Failed to load child profile');
        setIsLoading(false);
      }
    };

    fetchChildProfile();
  }, [profileId, profiles, getProfile]);

  const handleSubmitSuccess = async () => {
    // Refresh profiles after successful update
    await refreshProfiles();
    // Navigate to the dashboard
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="container">
          <FormHeader>
            <Link to={`/child/${profileId}`} className="back-link">
              <i className="fas fa-arrow-left me-2"></i> Back to Child Profile
            </Link>
          </FormHeader>
          
          <LoadingContainer>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading child profile...</p>
          </LoadingContainer>
        </div>
      </PageContainer>
    );
  }

  if (loadError || !childProfile) {
    return (
      <PageContainer>
        <div className="container">
          <FormHeader>
            <Link to="/dashboard" className="back-link">
              <i className="fas fa-arrow-left me-2"></i> Back to Dashboard
            </Link>
          </FormHeader>
          
          <ErrorContainer>
            <h3>Error Loading Profile</h3>
            <p>{loadError || 'Could not find the requested profile'}</p>
            <Link to="/dashboard" className="btn btn-primary">
              Return to Dashboard
            </Link>
          </ErrorContainer>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="container">
        <FormHeader>
          <Link to={`/child/${profileId}`} className="back-link">
            <i className="fas fa-arrow-left me-2"></i> Back to Child Profile
          </Link>
        </FormHeader>
        
        <BabyProfileForm 
          initialValues={childProfile} 
          onSubmitSuccess={handleSubmitSuccess} 
          isEditing={true}
        />
      </div>
    </PageContainer>
  );
};

export default EditChild;
